'use client';

import { Children, type CSSProperties, type PropsWithChildren, useCallback, useEffect, useId, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { AnimatePresence, motion, type Transition } from 'motion/react';
import { buildResponsiveProps } from '../../util/responsive';
import { sanitizeCSSVariables } from '../../util/style';
import { VERTICAL_STACK_COMPONENT_STYLE_NAME, type VerticalStackProps } from '../alignment/vertical-stack';
import verticalStackStyles from '../alignment/vertical-stack.module.scss';
import styles from './success-check.module.scss';

const ANIMATION_DURATION = 0.4; // seconds

const POST_ANIMATION_ITEM_DELAY = 0.2; // seconds - delay before each child starts animating
const POST_ANIMATION_ITEM_INTERVAL_DELAY = 0.8; // seconds - delay between children appearing
const POST_ANIMATION_ITEM_ANIMATION_DURATION = 0.8; // seconds

const POST_ANIMATION_ITEM_TRANSITION = { delay: POST_ANIMATION_ITEM_DELAY, duration: POST_ANIMATION_ITEM_ANIMATION_DURATION, ease: 'easeOut' } as const satisfies Transition;
const POST_ANIMATION_ITEM_INITIAL = { opacity: 0, height: 0, y: 'var(--ixf-space-2)' };
const POST_ANIMATION_ITEM_FINAL = { opacity: 1, height: 'auto', y: 0 };

const CHECKMARK_TRANSITION = { delay: ANIMATION_DURATION, duration: ANIMATION_DURATION, ease: 'easeInOut' } as const satisfies Transition;
const CHECKMARK_INITIAL = { strokeDashoffset: 1 };
const CHECKMARK_FINAL = { strokeDashoffset: 0 };

const RING_TRANSITION = { duration: ANIMATION_DURATION, ease: 'easeInOut' } as const satisfies Transition;
const RING_FINAL = { strokeDashoffset: 0 };

interface CheckmarkIllustrationProps {
  className?: string;
  id: string;
  height: number;
  onAnimationEnd: VoidFunction;
}

function CheckmarkIllustration({ className, height, id, onAnimationEnd, ...props }: CheckmarkIllustrationProps) {
  const filterId = `checkmark_filter_${id}`;
  const linearGradientId = `checkmark_linear_gradient_${id}`;
  const shadowEffectId = `checkmark_shadow_effect_${id}`;

  return (
    <motion.svg
      className={clsx(styles.checkmark, className)}
      fill="none"
      height={height}
      viewBox="0 0 48 48"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <title>Check icon indicating success</title>
      <g filter={`url(#${filterId})`}>
        <motion.path
          animate={CHECKMARK_FINAL}
          d="M12.6 21.7765 L22.681 31.8575 L39.1213 15.4172"
          initial={CHECKMARK_INITIAL}
          onAnimationComplete={onAnimationEnd}
          pathLength={1}
          stroke={`url(#${linearGradientId})`}
          strokeDasharray={1} // total length
          strokeDashoffset={1} // start fully hidden
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={5}
          transition={CHECKMARK_TRANSITION}
        />
      </g>

      <defs>
        <filter colorInterpolationFilters="sRGB" filterUnits="userSpaceOnUse" height="23.4402" id={filterId} width="31.5212" x="10.1" y="12.9172">
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" mode="normal" result="shape" />
          <feColorMatrix in="SourceAlpha" result="hardAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" />
          <feOffset dy="2" />
          <feGaussianBlur stdDeviation="1" />
          <feComposite in2="hardAlpha" k2="-1" k3="1" operator="arithmetic" />
          <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.5 0" />
          <feBlend in2="shape" mode="normal" result={shadowEffectId} />
        </filter>
        <linearGradient gradientUnits="userSpaceOnUse" id={linearGradientId} x1="12.6" x2="37.7415" y1="23.6374" y2="29.527">
          <stop className={styles.stopPrimary} />
          <stop className={styles.stopTertiary} offset="0.25" />
          <stop className={styles.stopSecondary} offset="0.5" />
          <stop className={styles.stopQuaternary} offset="0.75" />
          <stop className={styles.stopDanger} offset="1" />
        </linearGradient>
      </defs>
    </motion.svg>
  );
}

interface RingIllustrationProps {
  className?: string;
  id: string;
  height: number;
}

function RingIllustration({ className, height, id }: RingIllustrationProps) {
  const filterId = `ring_filter_${id}`;
  const linearGradientId = `ring_linear_gradient_${id}`;
  const innerShadowResultId = `inner_shadow_result_${id}`;
  const strokeWidth = 6;
  const { circumference, initial } = useMemo(() => {
    const c = 2 * Math.PI * (height / 2);

    return {
      circumference: c,
      initial: { strokeDashoffset: c },
    };
  }, [height]);

  return (
    <motion.svg className={clsx(styles.ring, className)} fill="none" height={height} viewBox="0 0 86 86" xmlns="http://www.w3.org/2000/svg">
      <title>Decorative ring icon</title>
      <g filter={`url(#${filterId})`}>
        <motion.rect
          animate={RING_FINAL}
          height={height - strokeWidth}
          initial={initial}
          rx={38}
          stroke={`url(#${linearGradientId})`}
          strokeDasharray={circumference}
          strokeDashoffset={circumference}
          strokeWidth={strokeWidth}
          transition={RING_TRANSITION}
          width={height - strokeWidth}
          x={3}
          y={3}
        />
      </g>

      <defs>
        <filter colorInterpolationFilters="sRGB" filterUnits="userSpaceOnUse" height="88" id={filterId} width="86" x="0" y="0">
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" mode="normal" result="shape" />
          <feColorMatrix in="SourceAlpha" result="hardAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" />
          <feOffset dy="2" />
          <feGaussianBlur stdDeviation="1" />
          <feComposite in2="hardAlpha" k2="-1" k3="1" operator="arithmetic" />
          <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.5 0" />
          <feBlend in2="shape" mode="normal" result={innerShadowResultId} />
        </filter>

        <linearGradient gradientUnits="userSpaceOnUse" id={linearGradientId} x1="3" x2="81.3478" y1="43" y2="54.3774">
          <stop className={styles.stopPrimary} />
          <stop className={styles.stopTertiary} offset="0.25" />
          <stop className={styles.stopSecondary} offset="0.5" />
          <stop className={styles.stopQuaternary} offset="0.75" />
          <stop className={styles.stopDanger} offset="1" />
        </linearGradient>
      </defs>
    </motion.svg>
  );
}

const CONTAINER_SIZE = 86;
const CHECK_SIZE = 48;

interface SuccessCheckProps extends Pick<VerticalStackProps, 'align' | 'gap' | 'inlineAlign'> {
  className?: string;
  onAnimationEnd?: VoidFunction;
  style?: CSSProperties;
}

export function SuccessCheck({ className, children, onAnimationEnd, style: providedStyle, ...props }: PropsWithChildren<SuccessCheckProps>) {
  const { align, gap, inlineAlign, ...rest } = props;
  const childrenArray = Children.toArray(children);
  const childrenCount = childrenArray.length;
  const [visibleChildrenCount, setVisibleChildrenCount] = useState(0);
  const [isAnimationDone, setIsAnimationDone] = useState(false);
  const id = useId();
  const gapStyle = useMemo(() => buildResponsiveProps(VERTICAL_STACK_COMPONENT_STYLE_NAME, 'gap', gap, 'space'), [gap]);
  const style = useMemo(
    () =>
      sanitizeCSSVariables({
        '--ixf-success-check-illustration-size': `${CONTAINER_SIZE}px`,
        ...gapStyle,
        ...providedStyle,
      } as CSSProperties),
    [providedStyle, gapStyle],
  );
  const stackClassName = clsx(
    verticalStackStyles.verticalStack,
    className,
    verticalStackStyles[`align-${align}`],
    verticalStackStyles[`inline-align-${inlineAlign}`],
  );

  const handleAnimationDone = useCallback(() => {
    setIsAnimationDone(true);
    onAnimationEnd?.();
  }, [onAnimationEnd]);

  // When animation is done, start showing children one by one
  useEffect(() => {
    let interval: number | undefined;

    if (isAnimationDone && childrenCount) {
      let currentIndex = 0;

      function incrementChildrenCount() {
        setVisibleChildrenCount((prev) => prev + 1);
        currentIndex += 1;
      }

      incrementChildrenCount();

      interval = window.setInterval(() => {
        if (currentIndex < childrenCount) {
          incrementChildrenCount();
        } else {
          clearInterval(interval);
        }
      }, POST_ANIMATION_ITEM_INTERVAL_DELAY * 1000);
    }

    return () => {
      if (interval) {
        window.clearInterval(interval);
      }
    };
  }, [isAnimationDone, childrenCount]);

  return (
    <motion.div className={clsx(styles.successCheck, stackClassName, className)} style={style} {...rest}>
      <div className={styles.illustrationContainer}>
        <RingIllustration height={CONTAINER_SIZE} id={id} />
        <CheckmarkIllustration height={CHECK_SIZE} id={id} onAnimationEnd={handleAnimationDone} />
      </div>

      {isAnimationDone ? (
        <motion.div className={stackClassName} style={gapStyle}>
          <AnimatePresence>
            {childrenArray.slice(0, visibleChildrenCount).map((child, index) => (
              <motion.div
                animate={POST_ANIMATION_ITEM_FINAL}
                initial={POST_ANIMATION_ITEM_INITIAL}
                key={`child-${index}`}
                transition={POST_ANIMATION_ITEM_TRANSITION}
              >
                {child}
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      ) : null}
    </motion.div>
  );
}
